'use client';

import { Button, ButtonGroup, Datepicker, Drawer, Heading, TextField, TextPassage } from '@petsmart-ui/sparky';
import Cookies from 'js-cookie';
import moment, { type Moment } from 'moment';
import { type InputEvent, useState } from 'react';
import invariant from 'tiny-invariant';

import { config } from './config';
import { dateToAz, validateTime } from './utils';
import './PreviewDrawer.scss';

interface SitePreviewDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    previewDate?: string;
}
export function PreviewDrawer({ previewDate, isOpen, onClose }: SitePreviewDrawerProps) {
    const [date, setDate] = useState(() => {
        if (previewDate) {
            return moment(dateToAz(previewDate));
        }
        return null;
    });

    const [time, setTime] = useState(date?.format('HH:mm'));

    const onDateChange = (newDate: Moment | null) => {
        setDate(newDate);

        if (!time) {
            setTime('00:00');
        }
    };

    const onTimeChange = ({ currentTarget }: InputEvent<HTMLInputElement>) => {
        if (validateTime(currentTarget.value)) {
            setTime(currentTarget.value);
        }
    };

    const removePreviewParam = () => {
        const url = new URL(globalThis.location.href);
        url.searchParams.delete('_preview');
        globalThis.history.replaceState(null, '', url);
    };

    const onReset = () => {
        setDate(null);
        setTime(undefined);
        Cookies.remove('x-petm-preview');
        removePreviewParam();
        globalThis.location.reload();
        onClose();
    };

    const onPreview = () => {
        invariant(date && time, 'date && time must be a defined');
        const [hours, minutes] = time.split(':');
        date.set({ hour: Number.parseInt(hours, 10), minute: Number.parseInt(minutes, 10) });
        Cookies.set('x-petm-preview', JSON.stringify({ previewDate: date.format('YYYY-MM-DDTHH:mm:ss-07:00') }));
        removePreviewParam();
        globalThis.location.reload();
        onClose();
    };

    return (
        <Drawer isActive={isOpen} onClose={onClose}>
            <Drawer.Header>
                <Heading tagName="h3">Site Preview</Heading>
            </Drawer.Header>
            <Drawer.Body>
                <TextPassage>Date / Time</TextPassage>

                <div className="site-preview-pickers">
                    <Datepicker
                        date={date}
                        label="Date"
                        placeholder=""
                        numberOfMonths={1}
                        onDateChange={onDateChange}
                        isOutsideRange={() => false}
                        withFullScreenPortal={false}
                    />

                    <TextField
                        label="Time"
                        mask={config}
                        defaultValue={time ?? date?.format('HH:mm')}
                        onInput={onTimeChange}
                    />
                </div>
                <TextPassage className="site-preview-description">Time in: Arizona/Phoenix</TextPassage>
            </Drawer.Body>
            <Drawer.Footer>
                <ButtonGroup>
                    <Button text="Reset" onClick={onReset} fullWidth />
                    <Button text="Preview" variant="primary" onClick={onPreview} disabled={!date} fullWidth />
                </ButtonGroup>
            </Drawer.Footer>
        </Drawer>
    );
}
